// Pure (tiptap-free) https-only guard for iframe embed `src` attributes.
//
// Iframe embed attrs (youtube/pornHub/redGifs `src`) come from persisted document JSON, which an
// attacker can craft directly — it is not limited to what the editor produces. renderHTML feeds the
// served markup, and SafeHtmlStringRenderer escapes attribute metacharacters but does NOT neutralize a
// URL *scheme*: a `javascript:` / `data:` value survives escaping and renders as a live, script-capable
// `<iframe src>`. So any URL that lands in an iframe `src` must be protocol-restricted here, mirroring
// LinkCard's `safeLinkUrl`. Embeds are stricter than links: https-only (no plain http, no data:).
//
// Returns the input url when it parses to an `https:` URL; returns "" otherwise (empty, malformed,
// non-https). The three embed nodes emit the `<iframe>` child only when this returns a non-empty string.
export function safeIframeEmbedSrc(value: string | null | undefined): string {
  const normalized = typeof value === 'string' ? value.trim() : ''
  if (!normalized) {
    return ''
  }

  try {
    // Parse WITHOUT a base URL so only absolute URLs are accepted: a relative or protocol-relative
    // value (`//evil.example`, `not a url`) throws here rather than silently resolving to https. Real
    // provider embed URLs are always absolute https, so this only rejects hostile/malformed input.
    const parsed = new URL(normalized)
    return parsed.protocol === 'https:' ? normalized : ''
  } catch {
    return ''
  }
}

// The looser sibling, for a URL that lands in an `<img src>` rather than an `<iframe src>`.
//
// Shared by all three image-bearing nodes — the bare `image` node, `LinkCard`'s preview thumbnail and
// `ArgumentEmbed`'s author avatar. It lived as an identical copy in the latter two until 2026-08-08
// (register finding F12), while the `image` node — by far the most common of the three — had no guard
// at all: `@tiptap/extension-image`'s `renderHTML` is `["img", mergeAttributes(...)]` with no
// validation, and `SafeHtmlStringRenderer` escapes an attribute VALUE without neutralizing its scheme.
//
// Three deliberate differences from `safeIframeEmbedSrc`, each of which is a real published shape:
//   - a base URL is supplied, so a same-origin RELATIVE path resolves and is kept. Seavien's own
//     images are `/image/user/<name>/<id>/<size>`, so rejecting relatives would blank almost every
//     image in the product. The base is an unroutable placeholder host that is never emitted — only
//     the ORIGINAL string is returned, never the resolved one;
//   - plain `http:` is allowed, because imported content legitimately carries it and an image is not
//     a script-capable context;
//   - `data:image/…` is allowed, because both other call sites use it for preview thumbnails and the
//     package synthesizes such placeholders itself (`ExternalMediaPreview`). Note this means the
//     `image` node's `allowBase64: false` option remains a statement about `parseHTML` matching (the
//     paste path) and not about what a crafted `source_json` may render.
//
// Returns "" for anything else — notably `javascript:` and non-image `data:` — and callers omit the
// attribute or the element entirely rather than emitting an empty one.
export function safeImageUrl(value: unknown): string {
  const normalized = typeof value === 'string' ? value.trim() : ''
  if (!normalized) {
    return ''
  }
  if (/^data:image\//i.test(normalized)) {
    return normalized
  }
  try {
    const parsed = new URL(normalized, 'https://invalid.example')
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? normalized : ''
  } catch {
    return ''
  }
}
